import type {
  Currency,
  ExpenseCategory,
  ExpenseResponseDto,
  GroupMemberResponseDto,
  MemberType,
} from "@tabipla/shared";

/**
 * Draft state for expense form
 */
export interface ExpenseFormDraft {
  amount: string;
  /** Currency for this expense */
  currency: Currency;
  description: string;
  category: ExpenseCategory;
  date: string;
  splitEqually: boolean;
  selectedMemberKeys: string[];
  customAmounts: Record<string, string>;
  /** The member key of the payer (memberType:memberId format) */
  payerKey: string;
}

/**
 * Create a composite key for member lookup (memberType:memberId)
 */
export function memberKey(memberId: string, memberType: MemberType): string {
  return `${memberType}:${memberId}`;
}

/**
 * Parse member key back to memberId and memberType
 */
export function parseMemberKey(key: string): { memberId: string; memberType: MemberType } {
  const [memberType, memberId] = key.split(":") as [MemberType, string];
  return { memberId, memberType };
}

/**
 * Create initial draft with default values
 * @param currentUserKey - Optional key for current user to set as default payer
 */
export function getInitialDraft(currentUserKey?: string): ExpenseFormDraft {
  return {
    amount: "",
    currency: "JPY",
    description: "",
    category: "food", // 最も一般的なカテゴリをデフォルトに
    date: new Date().toISOString().split("T")[0],
    splitEqually: true,
    selectedMemberKeys: [],
    customAmounts: {},
    payerKey: currentUserKey || "",
  };
}

/**
 * Convert expense to draft format for editing
 */
export function expenseToDraft(expense: ExpenseResponseDto): ExpenseFormDraft {
  return {
    amount: expense.amount.toString(),
    currency: expense.currency,
    description: expense.description,
    category: expense.category,
    date: new Date(expense.date).toISOString().split("T")[0],
    splitEqually: false, // When editing, show actual amounts
    selectedMemberKeys: expense.shares.map((s) => memberKey(s.memberId, s.memberType)),
    customAmounts: expense.shares.reduce(
      (acc, s) => {
        acc[memberKey(s.memberId, s.memberType)] = s.amount.toString();
        return acc;
      },
      {} as Record<string, string>
    ),
    payerKey: memberKey(expense.paidById, expense.paidByType),
  };
}

/**
 * Calculate equal split with remainder distribution
 * The remainder is distributed to the first N members (1 yen each)
 */
export function calculateEqualSplit(
  totalAmount: number,
  memberCount: number
): { baseAmount: number; remainder: number } {
  if (memberCount <= 0) {
    return { baseAmount: 0, remainder: 0 };
  }
  const baseAmount = Math.floor(totalAmount / memberCount);
  const remainder = totalAmount - baseAmount * memberCount;
  return { baseAmount, remainder };
}

/**
 * Calculate share amount for a member in equal split
 * @param index - The index of the member (0-based)
 * @param baseAmount - The base amount per member
 * @param remainder - The remainder to distribute
 */
export function getShareAmount(index: number, baseAmount: number, remainder: number): number {
  return baseAmount + (index < remainder ? 1 : 0);
}

/**
 * Calculate total of custom amounts for selected members
 */
export function calculateTotalCustomAmount(
  customAmounts: Record<string, string>,
  selectedMemberKeys: Set<string>
): number {
  return Object.entries(customAmounts)
    .filter(([key]) => selectedMemberKeys.has(key))
    .reduce((sum, [, value]) => sum + (parseInt(value, 10) || 0), 0);
}

/**
 * Validate expense form
 */
export function isValidExpenseForm(
  amount: string,
  selectedMemberCount: number,
  splitEqually: boolean,
  totalCustomAmount: number
): boolean {
  const parsedAmount = parseInt(amount, 10);
  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return false;
  }
  if (selectedMemberCount <= 0) {
    return false;
  }
  if (!splitEqually && totalCustomAmount !== parsedAmount) {
    return false;
  }
  return true;
}

/**
 * Check if member joined after the expense date
 */
export function memberJoinedAfterExpenseDate(memberJoinedAt: Date, expenseDate: Date): boolean {
  // Compare only the date part (year, month, day) to ignore time
  const joined = new Date(
    memberJoinedAt.getFullYear(),
    memberJoinedAt.getMonth(),
    memberJoinedAt.getDate()
  );
  const expense = new Date(
    expenseDate.getFullYear(),
    expenseDate.getMonth(),
    expenseDate.getDate()
  );
  return joined > expense;
}

/**
 * Filter members who joined on or before the expense date
 */
export function filterEligibleMembers(
  members: GroupMemberResponseDto[],
  expenseDate: Date
): GroupMemberResponseDto[] {
  return members.filter((m) => {
    const joinedAt = new Date(m.joinedAt);
    return !memberJoinedAfterExpenseDate(joinedAt, expenseDate);
  });
}

/**
 * Check if draft has user-entered content (not just defaults)
 */
export function draftHasUserContent(draft: ExpenseFormDraft): boolean {
  return draft.amount.trim() !== "" || draft.description.trim() !== "";
}
